const INSTALL_INSTANCE_KEY = "uniair_app_install_instance_id";
const INSTALL_SESSION_KEY = "uniair_app_install_session_id";
const INSTALL_STATE_EVENT = "uniair-app-install-state";

let deferredInstallPrompt = null;
let installedThisSession = false;
let initialized = false;

const safeGet = (storage, key) => {
  try {
    return storage?.getItem(key) || null;
  } catch {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // App-install tracking must never block the customer journey.
  }
};

const createUuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

const getOrCreateToken = (storage, key) => {
  const existing = safeGet(storage, key);
  if (existing) return existing;

  const value = createUuid();
  safeSet(storage, key, value);
  return value;
};

export const getInstallInstanceId = () => {
  if (typeof window === "undefined") return "";
  return getOrCreateToken(window.localStorage, INSTALL_INSTANCE_KEY);
};

export const getInstallSessionId = () => {
  if (typeof window === "undefined") return "";
  return getOrCreateToken(window.sessionStorage, INSTALL_SESSION_KEY);
};

export const isIosDevice = () => {
  if (typeof navigator === "undefined") return false;

  const userAgent = String(navigator.userAgent || "");
  const classicIos = /iPhone|iPad|iPod/i.test(userAgent);
  const modernIpad =
    navigator.platform === "MacIntel" && Number(navigator.maxTouchPoints || 0) > 1;

  return classicIos || modernIpad;
};

export const isMobileDevice = () => {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return false;
  }

  const userAgent = String(navigator.userAgent || "");
  return (
    isIosDevice() ||
    /Android|Mobile|IEMobile|Opera Mini/i.test(userAgent) ||
    Number(window.innerWidth || 0) < 900
  );
};

export const detectPlatform = () => {
  if (typeof navigator === "undefined") return "unknown";
  const userAgent = String(navigator.userAgent || "").toLowerCase();

  if (isIosDevice()) return "ios";
  if (userAgent.includes("android")) return "android";
  if (userAgent.includes("windows")) return "windows";
  if (userAgent.includes("mac os") || userAgent.includes("macintosh")) return "macos";
  if (userAgent.includes("linux")) return "linux";
  return "other";
};

export const detectBrowser = () => {
  if (typeof navigator === "undefined") return "unknown";
  const userAgent = String(navigator.userAgent || "").toLowerCase();

  if (userAgent.includes("edg/")) return "edge";
  if (userAgent.includes("samsungbrowser")) return "samsung";
  if (userAgent.includes("crios") || userAgent.includes("chrome/")) return "chrome";
  if (userAgent.includes("fxios") || userAgent.includes("firefox/")) return "firefox";
  if (
    userAgent.includes("safari/") &&
    !userAgent.includes("chrome/") &&
    !userAgent.includes("crios")
  ) {
    return "safari";
  }
  return "other";
};

export const getDisplayMode = () => {
  if (typeof window === "undefined") return "unknown";

  const modes = [
    "window-controls-overlay",
    "standalone",
    "minimal-ui",
    "fullscreen",
  ];

  for (const mode of modes) {
    try {
      if (window.matchMedia?.(`(display-mode: ${mode})`).matches) {
        return mode;
      }
    } catch {
      // Ignore unsupported media modes.
    }
  }

  if (typeof navigator !== "undefined" && navigator.standalone === true) {
    return "standalone";
  }

  return "browser";
};

export const isStandaloneMode = () => {
  if (typeof navigator !== "undefined" && navigator.standalone === true) {
    return true;
  }

  const mode = getDisplayMode();
  return mode === "standalone" || mode === "window-controls-overlay";
};

const emitState = (kind, detail = {}) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(INSTALL_STATE_EVENT, {
      detail: {
        kind,
        ...detail,
        state: getInstallState(),
      },
    }),
  );
};

export const getInstallState = () => ({
  canPrompt: Boolean(deferredInstallPrompt),
  standalone: isStandaloneMode(),
  installedThisSession,
  ios: isIosDevice(),
  mobile: isMobileDevice(),
  platform: detectPlatform(),
  browser: detectBrowser(),
  displayMode: getDisplayMode(),
});

export const initializeAppInstallCapture = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    emitState("prompt_available");
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installedThisSession = true;
    emitState("appinstalled");
  });

  try {
    const standaloneMedia = window.matchMedia("(display-mode: standalone)");
    const onDisplayModeChange = () => emitState("display_mode_changed");

    if (standaloneMedia.addEventListener) {
      standaloneMedia.addEventListener("change", onDisplayModeChange);
    } else if (standaloneMedia.addListener) {
      standaloneMedia.addListener(onDisplayModeChange);
    }
  } catch {
    // Older browsers can still use the static standalone checks.
  }
};

export const subscribeToInstallState = (listener) => {
  if (typeof window === "undefined") return () => {};

  const handler = (event) => {
    listener?.(event?.detail || { state: getInstallState() });
  };

  window.addEventListener(INSTALL_STATE_EVENT, handler);
  return () => window.removeEventListener(INSTALL_STATE_EVENT, handler);
};

export const promptForAppInstall = async () => {
  if (!deferredInstallPrompt) {
    return { outcome: "unavailable", platform: "" };
  }

  const promptEvent = deferredInstallPrompt;
  deferredInstallPrompt = null;

  const result = await promptEvent.prompt();
  emitState("prompt_resolved", {
    outcome: result?.outcome || "unknown",
    platform: result?.platform || "",
  });

  return {
    outcome: result?.outcome || "unknown",
    platform: result?.platform || "",
  };
};
